/**
 * Work-type vs. location separation for Cost Library matching and Combined-view merging.
 *
 * Quote line items are often named with their project location so builders know
 * which part of the job they cover — "Porch Skim Finish", "Board and Skim - Porch",
 * "Extension Floor Screed". From a pricing perspective the location is irrelevant:
 * "Porch Skim Finish" is priced as "Skim Finish". This module strips location /
 * room qualifier words from an item name FOR MATCHING AND CANONICALISATION ONLY —
 * quote display names are never changed.
 *
 * Guards:
 *  - Kitchen/bathroom fit-out categories are never stripped: there the room word
 *    IS the work ("Kitchen fitting", "Bathroom installation").
 *  - Stripping never leaves fewer than `minMeaningfulTokens` tokens (default 2).
 */

/** Room / location qualifier words. Deliberately conservative: words that can be
 * part of the work itself ("ground" floor slab, "first" fix, "garden" wall,
 * "dormer") are NOT listed. */
export const LOCATION_QUALIFIER_WORDS = new Set([
  "porch",
  "extension",
  "loft",
  "attic",
  "conservatory",
  "garage",
  "outbuilding",
  "annexe",
  "annex",
  "basement",
  "cellar",
  "hallway",
  "hall",
  "landing",
  "bedroom",
  "lounge",
  "cloakroom",
  "ensuite",
  "en-suite",
  "utility",
  "kitchen",
  "bathroom",
  "front",
  "rear",
  "side",
  "upstairs",
  "downstairs",
]);

/** Categories where room words are the object of the work — never strip. */
const FIT_OUT_CATEGORY_PATTERN = /kitchen|bathroom/;

/** Tokens that don't count as "meaningful" when checking the min-token guard. */
const NON_MEANINGFUL = /^(-|–|—|:|,|\/|&|to|the|and|of|in|on|for|a|an|per|new|existing)$/i;

/**
 * Strip location/room qualifier tokens from an item name, preserving original
 * casing and word order of the remaining tokens. Returns the ORIGINAL name
 * unchanged when:
 *  - the category is a kitchen/bathroom fit-out category, or
 *  - nothing would be stripped, or
 *  - stripping would leave fewer than `minMeaningfulTokens` meaningful tokens.
 */
export function stripLocationQualifiers(
  name: string,
  category?: string,
  opts?: {
    /**
     * Minimum meaningful tokens that must remain after stripping (default 2 —
     * the safe setting for Cost Library matching). The Combined-view display
     * merge passes 1: "Porch Foundations" → "Foundations" is desirable there
     * because merging additionally requires ≥2 items sharing category + unit.
     */
    minMeaningfulTokens?: number;
  },
): string {
  if (!name || !name.trim()) return name;
  const cat = (category || "").toLowerCase().trim();
  if (FIT_OUT_CATEGORY_PATTERN.test(cat)) return name;

  const minTokens = opts?.minMeaningfulTokens ?? 2;
  const rawTokens = name.trim().split(/\s+/);
  const kept = rawTokens.filter(t => {
    const w = t.toLowerCase().replace(/[^a-z-]/g, "");
    return !LOCATION_QUALIFIER_WORDS.has(w);
  });
  if (kept.length === rawTokens.length) return name;

  const meaningful = kept.filter(t => /[a-z0-9]/i.test(t) && !NON_MEANINGFUL.test(t));
  if (meaningful.length < minTokens) return name;

  return kept
    .join(" ")
    .replace(/^\s*[-–—:,]+\s*/, "")
    .replace(/\s*[-–—:,]+\s*$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * The leading task phrase of a line-item description — descriptions typically
 * open with the core task ("Skim finish to porch walls and ceiling..."). Used as
 * an additional matching signal. Returns up to the first `maxWords` words, cut
 * at the first sentence/clause boundary.
 */
export function leadingTaskPhrase(description: string | null | undefined, maxWords = 8): string {
  if (!description) return "";
  const firstClause = description.split(/[.;:\n]/)[0] || "";
  return firstClause.trim().split(/\s+/).slice(0, maxWords).join(" ");
}

// ---------------------------------------------------------------------------
// Combined-view line-item merging
// ---------------------------------------------------------------------------

/**
 * Coarse unit-dimension classifier — prevents merging items that measure
 * different things (m² of wall vs "each" of door).
 */
export function unitDimension(u: string | null | undefined): string {
  const n = (u || "").toLowerCase().replace(/²/g, "2").replace(/³/g, "3");
  if (/sq\s*m|sqm|m2|sq\s*ft|square/.test(n)) return "area";
  if (/linear|lin\s*m|\blm\b|\blf\b|metre run|meter run/.test(n)) return "linear";
  if (/m3|cubic/.test(n)) return "volume";
  if (/each|item|no\b|nr\b|unit/.test(n)) return "each";
  return n;
}

/** Filler tokens dropped when normalising a work-type name for equivalence:
 * generic suffix words that AI wording adds inconsistently ("Preliminaries And
 * Setup" vs "Preliminaries", "Decoration Finish" vs "Decoration"). Words that
 * carry the meaning of the work (removal, demolition, installation) are NOT
 * dropped — "Staircase Removal" must never equal "Staircase Installation". */
const MERGE_FILLER = /^(setup|finish|finishes|work|works)$/i;

/** Head nouns too generic to signal work-type equivalence on their own. */
const GENERIC_HEAD = /^(installation|install|item|allowance|alteration|adjustment|package|good|making|system|unit)$/i;

function singularize(t: string): string {
  if (t.length <= 3) return t;
  if (/ies$/.test(t)) return t.replace(/ies$/, "y");
  if (/sses$/.test(t) || /ss$/.test(t)) return t;
  return t.replace(/s$/, "");
}

/** Normalised meaningful tokens of an item name (location words stripped,
 * fillers dropped, plurals singularised, lowercase). */
export function workTypeTokens(name: string, category?: string): string[] {
  const stripped = stripLocationQualifiers(name || "", category, { minMeaningfulTokens: 1 });
  const tokens = stripped
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(t => t && !NON_MEANINGFUL.test(t) && !MERGE_FILLER.test(t))
    .map(singularize);
  // Never normalise down to nothing — fall back to the raw lowercase name.
  return tokens.length > 0 ? tokens : [(name || "").toLowerCase().trim()];
}

export interface MergeCandidate {
  name: string;
  category?: string | null;
  unit?: string | null;
  unitCost: number;
}

/**
 * Decide whether two line items describe the same work type and may be merged
 * in a Combined multi-scope view.
 *
 * Tier 1 — normalised names are identical and the unit dimension matches
 *          ("Preliminaries And Setup" ≡ "Porch Preliminaries").
 * Tier 2 — same category and unit dimension, unit rates within 2%, and either
 *          one token set contains the other ("Waste Removal Skips" ⊇ "Waste
 *          Skip") or both share the same non-generic head noun ("Sand Cement
 *          Screed" / "Floor Screed"). The rate check is the safety net that
 *          keeps genuinely different work (£130/m² cavity wall vs £45/m²
 *          study wall) from collapsing together.
 */
export function areEquivalentWorkItems(a: MergeCandidate, b: MergeCandidate): boolean {
  if (unitDimension(a.unit) !== unitDimension(b.unit)) return false;
  const ta = workTypeTokens(a.name, a.category ?? undefined);
  const tb = workTypeTokens(b.name, b.category ?? undefined);
  const sa = ta.join(" ");
  const sb = tb.join(" ");
  if (sa === sb) return true; // Tier 1

  // Tier 2
  const catA = (a.category || "").toLowerCase().trim();
  const catB = (b.category || "").toLowerCase().trim();
  if (!catA || catA !== catB) return false;
  const rateA = Number(a.unitCost) || 0;
  const rateB = Number(b.unitCost) || 0;
  const maxRate = Math.max(Math.abs(rateA), Math.abs(rateB));
  if (maxRate === 0 || Math.abs(rateA - rateB) / maxRate > 0.02) return false;

  const setA = new Set(ta);
  const setB = new Set(tb);
  const subset = ta.every(t => setB.has(t)) || tb.every(t => setA.has(t));
  if (subset) return true;

  const headA = ta[ta.length - 1];
  const headB = tb[tb.length - 1];
  return headA === headB && !GENERIC_HEAD.test(headA);
}

/**
 * Group line items into merge groups for a Combined multi-scope view. Returns
 * one group per output row: the original indices of its members plus the
 * cleanest display name (the shortest location-stripped member name).
 * Single-member groups are items with no equivalent partner.
 *
 * Each candidate is compared against the group's ANCHOR (first member) only —
 * equivalence is pairwise and not transitive (the 2% rate tolerance could
 * otherwise chain A≈B≈C where A and C differ by more), so anchor semantics
 * prevent chain over-merges.
 *
 * Callers must group core and optional items separately — merging an optional
 * item into a quoted row (or vice versa) would silently move cost across the
 * quoted-price boundary.
 */
// ---------------------------------------------------------------------------
// Item sub-group classification — collapsible headers within each stage of the
// quote editor. Each sub-group maps to a human-readable label, an icon, and a
// stage-specific display order defined in STAGE_SUB_GROUP_ORDER.
// ---------------------------------------------------------------------------

export type ItemSubGroupId =
  // ── Demolition ──────────────────────────────────────────────────────────────
  | "demolition_works"
  // ── Substructure ────────────────────────────────────────────────────────────
  | "ground_works"
  // ── Structure ───────────────────────────────────────────────────────────────
  | "masonry_works"
  | "structural_steel"
  | "thermal_insulation"
  // ── Roofing ─────────────────────────────────────────────────────────────────
  | "pitched_roofing"
  | "flat_roofing"
  | "roof_windows"
  | "fascias_guttering"
  // ── External Works ──────────────────────────────────────────────────────────
  | "windows_doors"
  // ── Services ────────────────────────────────────────────────────────────────
  | "first_fix_electrics"
  | "second_fix_electrics"
  | "first_fix_plumbing"
  | "second_fix_plumbing"
  | "heating"
  // ── Finishes ────────────────────────────────────────────────────────────────
  | "drylining_works"
  | "second_fix_joinery"
  | "making_good"
  | "decorative_finishes";

/** Backward-compat alias — prefer ItemSubGroupId in new code. */
export type ServiceSubGroupId = ItemSubGroupId;

/** Human-readable label for each sub-group (shown as collapsible header in UI). */
export const ITEM_SUB_GROUP_LABELS: Record<ItemSubGroupId, string> = {
  demolition_works:    "Demolition Works",
  ground_works:        "Ground Works",
  masonry_works:       "Masonry Works",
  structural_steel:    "Structural Steel",
  thermal_insulation:  "Thermal Insulation",
  pitched_roofing:     "Pitched Roofing",
  flat_roofing:        "Flat Roofing",
  roof_windows:        "Roof Windows & Lanterns",
  fascias_guttering:   "Fascias, Soffits & Guttering",
  windows_doors:       "Windows & Doors",
  first_fix_electrics: "First-Fix Electrics",
  second_fix_electrics:"Second-Fix Electrics",
  first_fix_plumbing:  "First-Fix Plumbing",
  second_fix_plumbing: "Second-Fix Plumbing",
  heating:             "Heating",
  drylining_works:     "Drylining Works",
  second_fix_joinery:  "Second-Fix Joinery",
  making_good:         "Making Good",
  decorative_finishes: "Decorative Finishes",
};

/** Backward-compat alias. */
export const SERVICE_SUB_GROUP_LABELS = ITEM_SUB_GROUP_LABELS;

/**
 * Ordered sub-groups per stage — drives the header sequence and item sort within
 * each stage. Items whose sub-group isn't in the stage list sort to the bottom.
 */
export const STAGE_SUB_GROUP_ORDER: Record<string, ItemSubGroupId[]> = {
  "Demolition":     ["demolition_works"],
  "Substructure":   ["ground_works"],
  "Structure":      ["masonry_works", "structural_steel", "thermal_insulation"],
  "Roofing":        ["pitched_roofing", "flat_roofing", "roof_windows", "fascias_guttering"],
  "External Works": ["windows_doors"],
  "Services":       ["first_fix_electrics", "second_fix_electrics", "first_fix_plumbing", "second_fix_plumbing", "heating"],
  "Finishes":       ["drylining_works", "second_fix_joinery", "making_good", "decorative_finishes"],
};

/** Backward-compat alias for code that only references the Services ordering. */
export const SERVICE_SUB_GROUP_ORDER: ItemSubGroupId[] = STAGE_SUB_GROUP_ORDER["Services"];

// ---------------------------------------------------------------------------
// Summary Quote section grouping — maps raw item.category keys to the five
// high-level sections shown in the quote editor. Used by the Quote View
// summary toggle, the public customer preview, and the PDF so all three
// surfaces stay consistent.
// ---------------------------------------------------------------------------

/** Canonical high-level section names used by the Summary Quote surfaces. */
export type CategorySection =
  | "Preparation"
  | "Demolition"
  | "Structure"
  | "Finishes"
  | "Services"
  | "Other";

/** Backward-compat alias — prefer CategorySection in new code. */
export type QuoteSectionName = CategorySection;

/** Canonical display order for summary quote sections. */
export const CATEGORY_SECTION_ORDER: CategorySection[] = [
  "Preparation",
  "Demolition",
  "Structure",
  "Finishes",
  "Services",
  "Other",
];

/**
 * Canonical summary section for each item sub-group. When an item resolves to
 * a sub-group, summary surfaces group it under this section regardless of the
 * item's raw category — otherwise the same sub-group label (e.g. "Demolition
 * Works") can appear under two different sections when raw categories differ.
 */
export const SUB_GROUP_SECTION: Record<ItemSubGroupId, CategorySection> = {
  demolition_works: "Demolition",
  ground_works: "Structure",
  masonry_works: "Structure",
  structural_steel: "Structure",
  thermal_insulation: "Structure",
  pitched_roofing: "Structure",
  flat_roofing: "Structure",
  roof_windows: "Structure",
  fascias_guttering: "Structure",
  windows_doors: "Structure",
  first_fix_electrics: "Services",
  second_fix_electrics: "Services",
  first_fix_plumbing: "Services",
  second_fix_plumbing: "Services",
  heating: "Services",
  drylining_works: "Finishes",
  second_fix_joinery: "Finishes",
  making_good: "Finishes",
  decorative_finishes: "Finishes",
};

/** Backward-compat alias. */
export const QUOTE_SECTION_ORDER = CATEGORY_SECTION_ORDER;

/**
 * Raw item.category key → high-level summary section.
 * Includes canonical COST_CATEGORIES keys plus common engine/legacy aliases
 * (the DB category column is unconstrained text, so the engine can emit
 * variants like steel_heavy, skip, stud_walls, bathroom, etc.).
 * Unknown categories fall back to keyword matching, then "Other",
 * via getCategorySection().
 */
export const CATEGORY_SECTION_MAP: Record<string, CategorySection> = {
  // ── Preparation ────────────────────────────────────────────────────────────
  preliminaries: "Preparation",
  scaffolding: "Preparation",
  scaffold: "Preparation",
  waste: "Preparation",
  skip: "Preparation",
  skip_hire: "Preparation",
  plant: "Preparation",
  propping: "Preparation",
  temporary_works: "Preparation",
  // ── Demolition ─────────────────────────────────────────────────────────────
  demolition: "Demolition",
  strip_out: "Demolition",
  // ── Structure ──────────────────────────────────────────────────────────────
  groundworks: "Structure",
  foundations: "Structure",
  brickwork: "Structure",
  blockwork: "Structure",
  steel: "Structure",
  steelwork: "Structure",
  steel_heavy: "Structure",
  steel_medium: "Structure",
  steel_post: "Structure",
  lintel: "Structure",
  padstone: "Structure",
  roofing: "Structure",
  roof_repair: "Structure",
  flat_roofing: "Structure",
  flat_roof: "Structure",
  pitched_roofing: "Structure",
  fascias_soffits: "Structure",
  fascias: "Structure",
  windows_doors: "Structure",
  windows: "Structure",
  doors: "Structure",
  external_doors: "Structure",
  bifold_doors: "Structure",
  insulation: "Structure",
  damp_proofing: "Structure",
  cladding: "Structure",
  external_works: "Structure",
  fire_protection: "Structure",
  // ── Finishes ───────────────────────────────────────────────────────────────
  plastering: "Finishes",
  render: "Finishes",
  internal_walls: "Finishes",
  stud_walls: "Finishes",
  drylining: "Finishes",
  carpentry: "Finishes",
  staircases: "Finishes",
  flooring: "Finishes",
  tiling: "Finishes",
  decorating: "Finishes",
  kitchens: "Finishes",
  kitchen: "Finishes",
  bathrooms: "Finishes",
  bathroom: "Finishes",
  making_good: "Finishes",
  // ── Services ───────────────────────────────────────────────────────────────
  ventilation: "Services",
  electrics: "Services",
  electrical: "Services",
  plumbing: "Services",
  pipework: "Services",
  heating: "Services",
  radiators: "Services",
  underfloor_heating: "Services",
  gas_works: "Services",
};

/**
 * Keyword fallback for free-form AI-assigned categories that aren't exact
 * COST_CATEGORIES keys (e.g. "Demolition and Strip Out", "structural steelwork",
 * "kitchen_unit"). Checked in order; first match wins.
 */
const CATEGORY_SECTION_KEYWORDS: [RegExp, CategorySection][] = [
  [/demolition|strip.?out|beam removal/, "Demolition"],
  [/prelim|scaffold|skip|waste|site (setup|preparation|cleanup)|dust control|debris|protection|access|temporary|propping|plant\b/, "Preparation"],
  [/electric|plumb|heating|ventilat|radiator|boiler|building_services|sanitaryware/, "Services"],
  [/plaster|drylin|decorat|paint|floor|tiling|kitchen|bathroom|carpentry|joinery|cabinetry|countertop|worktop|making.?good|internal.?(wall|partition)|stud|finish|render/, "Finishes"],
  [/steel|masonry|brick|block|concrete|foundation|groundwork|substructure|roof|window|door|insulation|damp|lintel|padstone|structural|drainage|cladding|external.?works|restraint|anchor/, "Structure"],
];

/**
 * Resolve an item's raw category key to its summary section. Tolerant of
 * case/whitespace and separator variants; free-form categories that don't
 * match the map exactly fall through to keyword matching, then "Other".
 */
export function getCategorySection(category: string | null | undefined): CategorySection {
  const cat = (category || "").trim().toLowerCase();
  if (!cat) return "Other";
  const key = cat.replace(/[\s&/-]+/g, "_");
  const exact = CATEGORY_SECTION_MAP[cat] ?? CATEGORY_SECTION_MAP[key];
  if (exact) return exact;
  for (const [pattern, section] of CATEGORY_SECTION_KEYWORDS) {
    if (pattern.test(cat)) return section;
  }
  return "Other";
}

// ── Pattern banks ────────────────────────────────────────────────────────────

function matchesAny(name: string, patterns: RegExp[]): boolean {
  return patterns.some(p => p.test(name));
}

const FIRST_FIX_ELEC  = [/\bfirst.?fix\b/i, /\bwir(ing|e)\b/i, /\bcabl(e|ing)\b/i, /\bcontainment\b/i, /\bconduit\b/i, /\btrunking\b/i, /\bback.?box/i, /\bchasing\b/i];
const SECOND_FIX_ELEC = [/\bsecond.?fix\b/i, /\bsocket\b/i, /\bswitch\b/i, /\bdownlight\b/i, /\blight.?fit/i, /\baccessor/i, /\bcertif/i, /\btesting?\b/i, /\bdetector\b/i, /\bextract(or)?\b/i, /\bpendant\b/i, /\bfan\b/i, /\balarm\b/i, /\bventilat/i, /\bmvhr\b/i];
const FIRST_FIX_PLMB  = [/\bfirst.?fix\b/i, /\bpipe(work)?\b/i, /\brough.?in\b/i, /\bsoil\b/i, /\bwaste.?pip/i, /\bhot.?cold\b/i, /\bsupply.?(run|pipe)\b/i];
const SECOND_FIX_PLMB = [/\bsecond.?fix\b/i, /\bwc\b/i, /\btoilet\b/i, /\bbasin\b/i, /\bsink\b/i, /\bbath\b/i, /\bshower\b/i, /\btap\b/i, /\bsanit/i, /\bfixture\b/i];
const HEATING_PAT     = [/\bradiator\b/i, /\bheating\b/i, /\bboiler\b/i, /\bunderfloor\b/i, /\bufh\b/i, /\btowel.?rail\b/i];

/** Second-fix finish-joinery name patterns (skirting, architraves, door linings …). */
const JOINERY_PAT = [
  /\bskirting\b/i, /\barchitrave\b/i, /\bdoor.?lining\b/i, /\bwindow.?board\b/i,
  /\bdoor.?set\b/i, /\bhandrail\b/i, /\bbalustrade\b/i, /\bmdf.?trim\b/i,
  /\bfinish.?joiner/i, /\bstair\b/i, /\binternal.?door\b/i, /\bpanel.?door\b/i,
  /\bnewel\b/i, /\bcornice\b/i, /\bdado.?rail\b/i,
];

/** Roof window / skylight / lantern name patterns. */
const ROOF_WINDOW_PAT = [
  /\bvelux\b/i, /\broof.?windows?\b/i, /\bskylights?\b/i, /\brooflights?\b/i,
  /\broof.?lights?\b/i, /\broof.?lanterns?\b/i, /\blanterns?\b/i, /\bsun.?tunnels?\b/i,
  /\bsky.?lanterns?\b/i,
];

/** Flat-roof name patterns — catches `roofing` category items that are flat. */
const FLAT_ROOF_PAT = [
  /\bflat.?roof\b/i, /\bepdm\b/i, /\bgrp\b/i, /\bfibreglass?\b/i,
  /\btorch.?on\b/i, /\bmineral.?felt\b/i, /\bbutyl\b/i, /\blead.?flat\b/i,
  /\brubber.?roof\b/i, /\bflat.?deck\b/i,
];

/** Fascia / soffit / guttering name patterns. */
const FASCIA_PAT = [
  /\bfascia\b/i, /\bsoffit\b/i, /\bgutter(ing)?\b/i, /\bdownpipe\b/i, /\bbargeboard\b/i,
];

/** Drylining / plastering name patterns (plasterboard, skim, beads …). */
const DRYLINING_PAT = [
  /\bplasterboard\b/i, /\bdry.?lin\b/i, /\bboarding\b/i, /\bskim\b/i,
  /\bbead\b/i, /\bcorner.?bead\b/i, /\bstud.?partition\b/i,
];

/**
 * Clear work-title evidence takes precedence over a broad AI category for
 * presentation only. Never changes the stored classification or pricing.
 * Installation accessories stay with their own trade (e.g. rooflight trimmers).
 */
export function isElectricalExtractorWork(category: string | null | undefined, name: string | null | undefined): boolean {
  const cat = (category ?? "").toLowerCase();
  const n = (name ?? "").toLowerCase();
  if (/demolition|strip.?out|temporary|propping|waste|skip|plant/.test(cat) ||
      /^(?:remove|removal|demolish|dismantle|strip|temporary|protect)\b/.test(n) ||
      /\b(?:hire|rental|dust|fume)\b/.test(n)) return false;
  return /\b(?:extractors?|(?:inline|extract|exhaust)[\s-]+fans?)\b/.test(n);
}

export function getWorkItemGroupingOverride(
  category: string | null | undefined,
  name: string | null | undefined,
): { stage: string; subGroup: ItemSubGroupId | null } | null {
  const cat = (category || "").trim().toLowerCase();
  const n = (name || "").trim().toLowerCase();
  // Removal and temporary-work lines must not become new installation work
  // merely because their title mentions the thing being removed/supported.
  if (/demolition|strip.?out|temporary|propping|waste|skip/.test(cat) ||
      /^(?:remove|removal|demolish|dismantle|strip|temporary|protect)\b/.test(n)) return null;
  if (isElectricalExtractorWork(cat, n)) {
    return { stage: "Services", subGroup: "second_fix_electrics" };
  }
  if (matchesAny(n, ROOF_WINDOW_PAT) &&
      /\b(?:trimmers?|rafters?|joists?|structural\s+timber|framing)\b/.test(n)) {
    return { stage: "Structure", subGroup: null };
  }
  if (/\b(?:dry[\s-]?lining|plasterboard(?:ing)?|skim(?:ming)?|plastering)\b/.test(n)) {
    return { stage: "Finishes", subGroup: "drylining_works" };
  }
  if (/\b(?:insulation|insulating)\b/.test(n)) {
    return { stage: "Structure", subGroup: "thermal_insulation" };
  }
  if (/\b(?:underfloor\s+heating|ufh)\b/.test(n) && /\b(?:screed|mesh|floor\s+finish)\b/.test(n)) {
    return { stage: "Finishes", subGroup: null };
  }
  // The room/fixture being worked around does not define the trade. Shower
  // tanking/tiling and basin boxing are finishes, not second-fix plumbing.
  if (/\b(?:bath(?:room)?|shower|wet[\s-]?(?:room|area)|basin|sink|wc|toilet)\b/.test(n) &&
      /\b(?:tanking|waterproof(?:ing)?|tiling|grout(?:ing)?|backer[\s-]?boards?|boxing|painting|decorating)\b/.test(n)) {
    return { stage: "Finishes", subGroup: /\b(?:painting|decorating)\b/.test(n) ? "decorative_finishes" : null };
  }
  // Named rough-in work is first-fix even when an AI category says Heating.
  // Do not reclassify heating circuits, fixture fit-off, or pipework finishes.
  if (/\b(?:pipework|(?:soil|waste)[\s-]+(?:pipes?|pipework|branch(?:es)?|runs?)|(?:first[\s-]?fix|rough[\s-]?in)[\s-]+plumbing)\b/.test(n) &&
      !matchesAny(n, HEATING_PAT) &&
      !/\b(?:second[\s-]?fix|boxing|casings?|cladding|painting|decorating|connect(?:ion)?|fit[\s-]?off)\b/.test(n)) {
    return { stage: "Services", subGroup: "first_fix_plumbing" };
  }
  if (matchesAny(n, ROOF_WINDOW_PAT) &&
      !/\b(?:flashings?|kerbs?|upstands?|openings?|blinds?|trims?)\b/.test(n)) {
    return { stage: "External Works", subGroup: "windows_doors" };
  }
  if (/\b(?:radiators?|boilers?|heating|underfloor\s+heating|ufh|towel[\s-]?rails?)\b/.test(n) &&
      !/\b(?:insulation|screed|mesh|floor\s+finish)\b/.test(n)) {
    return { stage: "Services", subGroup: "heating" };
  }
  return null;
}

/**
 * Classify a line item into a sub-group for collapsible grouping within its stage.
 * Returns null for items that don't belong to any sub-group (e.g. bundled legacy
 * items, pitched-roofing items, kitchen/bathroom fit-out, etc.).
 */
export function getItemSubGroup(
  category: string | null | undefined,
  name: string | null | undefined,
): ItemSubGroupId | null {
  const cat = (category || "").toLowerCase().trim();
  const n   = (name   || "").toLowerCase();
  const titleGrouping = getWorkItemGroupingOverride(cat, n);
  if (titleGrouping) return titleGrouping.subGroup;

  // ── Demolition stage ────────────────────────────────────────────────────────
  if (cat === "demolition" || cat === "waste" || cat === "skip") return "demolition_works";

  // ── Substructure stage ──────────────────────────────────────────────────────
  if (cat === "groundworks" || cat === "foundations") return "ground_works";
  // damp_proofing at substructure level; tanking/waterproofing is overridden
  // to Finishes by getItemStage so it never reaches this branch with that context.
  if (cat === "damp_proofing") return "ground_works";

  // ── Structure stage ─────────────────────────────────────────────────────────
  if (cat === "brickwork" || cat === "blockwork") return "masonry_works";
  if (cat === "steel" || cat === "steel_heavy" || cat === "steel_medium" ||
      cat === "steel_post" || cat === "lintel" || cat === "padstone" ||
      cat === "propping" || cat === "temporary_works") return "structural_steel";
  if (cat === "insulation") return "thermal_insulation";

  // ── Roofing stage ───────────────────────────────────────────────────────────
  if (cat === "flat_roofing" || cat === "flat_roof") return "flat_roofing";
  if (cat === "fascias_soffits" || cat === "fascias") return "fascias_guttering";
  // The generic "roofing" category is used for both pitched and flat items, and
  // also for roof repairs — disambiguate by name.
  if (cat === "roofing" || cat === "roof_repair" || (cat.startsWith("roof") && cat !== "roof")) {
    if (matchesAny(n, FASCIA_PAT))       return "fascias_guttering";
    if (matchesAny(n, ROOF_WINDOW_PAT)) return "roof_windows";
    if (matchesAny(n, FLAT_ROOF_PAT))   return "flat_roofing";
    return "pitched_roofing";
  }

  // ── External Works stage ────────────────────────────────────────────────────
  if (cat === "windows_doors" || cat === "windows" ||
      cat === "external_doors" || cat === "bifold_doors") return "windows_doors";

  // ── Finishes stage ──────────────────────────────────────────────────────────
  if (cat === "plastering" || cat === "render") return "drylining_works";
  if (cat === "internal_walls" || cat === "stud_walls") {
    // Stud/plasterboard partitions → drylining; block partitions stay ungrouped.
    if (matchesAny(n, DRYLINING_PAT) || /\bstud\b/i.test(n)) return "drylining_works";
    return null;
  }
  if (cat === "carpentry") {
    // First-fix structural carpentry is not finish joinery.
    if (/\bfirst.?fix\b/i.test(n)) return null;
    if (matchesAny(n, JOINERY_PAT))  return "second_fix_joinery";
    if (matchesAny(n, DRYLINING_PAT)) return "drylining_works";
    return null;
  }
  if (cat === "making_good") return "making_good";
  if (cat === "decorating")  return "decorative_finishes";

  // ── Services stage ──────────────────────────────────────────────────────────
  if (cat === "electrics" || cat === "electrical") {
    if (/\belectrical?\s+work\b/i.test(n)) return null; // bundled legacy item
    if (matchesAny(n, FIRST_FIX_ELEC))  return "first_fix_electrics";
    if (matchesAny(n, SECOND_FIX_ELEC)) return "second_fix_electrics";
    return "second_fix_electrics"; // default: most named items are second-fix
  }
  if (cat === "plumbing" || cat === "pipework") {
    if (/\bplumbing\s+work\b/i.test(n)) return null; // bundled legacy item
    if (/\bfirst.?fix\b/i.test(n))            return "first_fix_plumbing";
    if (/\bventilat|\bmvhr\b/i.test(n))        return "second_fix_electrics";
    if (matchesAny(n, HEATING_PAT))            return "heating";
    if (matchesAny(n, SECOND_FIX_PLMB))       return "second_fix_plumbing";
    if (matchesAny(n, FIRST_FIX_PLMB))        return "first_fix_plumbing";
    return "first_fix_plumbing"; // default: pipe runs
  }
  if (cat === "ventilation") return "second_fix_electrics";
  if (cat === "heating" || cat === "radiators" || cat === "underfloor_heating") return "heating";

  // ── Name-based fallback ─────────────────────────────────────────────────────
  // Catches items whose AI-assigned category doesn't match any branch above
  // (e.g. "Heating Extensions" under decorating, "WC Sanitary Installation"
  // under bathrooms). Applied last so category-based routing always wins.
  if (matchesAny(n, HEATING_PAT))      return "heating";
  if (matchesAny(n, SECOND_FIX_PLMB)) return "second_fix_plumbing";

  return null;
}

export function groupEquivalentLineItems(items: MergeCandidate[]): { indices: number[]; displayName: string }[] {
  const groups: { indices: number[]; anchor: MergeCandidate; displayName: string }[] = [];
  items.forEach((item, index) => {
    const stripped = stripLocationQualifiers(item.name || "", item.category ?? undefined, { minMeaningfulTokens: 1 });
    const group = groups.find(g => areEquivalentWorkItems(g.anchor, item));
    if (group) {
      group.indices.push(index);
      if (stripped.length < group.displayName.length) group.displayName = stripped;
    } else {
      groups.push({ indices: [index], anchor: item, displayName: stripped });
    }
  });
  return groups.map(({ indices, displayName }) => ({ indices, displayName }));
}
